import React, { Suspense, useContext } from "react";
import lazyWithRetry from "../../utils/lazyWithRetry";
import DesktopAppBarMenu from "./components/DesktopAppBarMenu";
import { ScreenSizeConfiguratorContext } from "../../../../library/shared-ui/components/ScreenSizeConfigurator";
import { Typography } from "@sezzle/sezzle-mui";
import { NavigationBarAndMenuProps } from "./types";
import { StyledAppBar, StyledToolBar, StyledAppBarMobileContainer, StyledAppBarLogoWrapper, StyledAppBarLogoContainer, StyledAppBarWebButtonsContentContainer, StyledAppBarWebContainer } from "./styles";
import ImageLink from "../ImageLink";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

const HamburgerMenu = lazyWithRetry(() => import("./components/HamburgerMenu"));
const ShopMenu = lazyWithRetry(() => import('./components/SearchMenu').then((module) => ({ default: module.SearchMenu })));

const NavigationBarAndMenu = ({
  featuredLinksMobileMenu,
  mobileNavigationLinks,
  featuredMobileSpot,
  ctaButtons,
  logo,
  navigationLinks,
  showSearchSuggestions,
  mobileShopMenu,
  searchSuggestions,
  mobileButton,
}: NavigationBarAndMenuProps) => {
  const isSSR = typeof window === 'undefined';

  const { screenConfig } = useContext(ScreenSizeConfiguratorContext);

  const renderMenuIcon = () => {
    if ((screenConfig.isMobile || screenConfig.isTablet) && !isSSR) {
      return (
        <Suspense fallback={<div />}>
          <HamburgerMenu
            featuredLinksMobileMenu={featuredLinksMobileMenu}
            mobileNavigationLinks={mobileNavigationLinks}
            featuredMobileSpot={featuredMobileSpot}
            ctaButtons={ctaButtons}
            screenConfig={screenConfig}
          />
        </Suspense>
      );
    }
    return null;
  };

  const renderLogo = () => {
    const logoUrl = logo?.imageUrl?.url || logo?.url;
    const assistiveText = logo?.assistiveText;

    const backgroundImage = logo?.image?.image?.gatsbyImageData?.images
      ?.fallback?.src
      ? `url(${logo?.image?.image?.gatsbyImageData?.images?.fallback?.src})`
      : `url(https:${logo?.image?.image?.file?.url})`;

    const img = getImage(logo?.image?.image?.gatsbyImageData);

    if (logoUrl) {
      return (
        <StyledAppBarLogoWrapper
          sx={{ backgroundImage: backgroundImage }}
        >
          <StyledAppBarLogoContainer>
            <a href={logo?.imageUrl?.url || logo?.url}>
              <GatsbyImage
                image={img}
              />
            </a>
          </StyledAppBarLogoContainer>
        </StyledAppBarLogoWrapper>
      );
    }
    return <Typography>{assistiveText}</Typography>;
  };

  const renderSearchIcon = () => {
    if (
      showSearchSuggestions &&
      (screenConfig.isMobile || screenConfig.isTablet) && !isSSR
    ) {
      return (
        <Suspense fallback={<div />}>
          <ShopMenu
            fields={mobileShopMenu}
            searchSuggestions={searchSuggestions}
            mobileButton={mobileButton}
            screenConfig={screenConfig}
          />
        </Suspense>
      );
    }
    return null;
  };

  const renderNavigationBar = () => {
    if ((!screenConfig.isMobile && !screenConfig.isTablet) && !isSSR) {
      return (
        <StyledAppBarWebButtonsContentContainer >
          {showSearchSuggestions && (
            <Suspense fallback={<div />}>
              <> {searchSuggestions} </>
            </Suspense>
          )}
          <DesktopAppBarMenu
            navigationLinks={navigationLinks}
            ctaButtons={ctaButtons}
          />
        </StyledAppBarWebButtonsContentContainer>
      );
    }
  };

  return (
    <StyledAppBar
      position="sticky"
      color="inherit"
    >
      <StyledToolBar >
        {screenConfig.isMobile || screenConfig.isTablet ? (
          <StyledAppBarMobileContainer>
            {renderMenuIcon()}
            {renderLogo()}
            {renderSearchIcon()}
          </StyledAppBarMobileContainer>
        ) : (
          <StyledAppBarWebContainer>
            {renderLogo()}
            {renderNavigationBar()}
          </StyledAppBarWebContainer>
        )}
      </StyledToolBar>
    </StyledAppBar>
  );
};

export default React.memo(NavigationBarAndMenu);
